import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {HandleType, NodeProps} from 'reactflow';

import SimpleBenchlingNode from 'ui-library/reactflow/nodes/SimpleBenchlingNode/SimpleBenchlingNode';
import {SimpleBenchlingNodeData} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/SimpleBenchlingNodeTypes';

import {InvisibleNodeHandle} from '../../../../ui-library/reactflow/handles/InvisibleNodeHandle';

export enum NodeType {
  SOURCE_PMEI = 'SOURCE_PMEI',
  DESTINATION_PMEI = 'DESTINATION_PMEI',
}

interface UnitOperationFlowchartNodeData {
  pmeiId: string;
  procedureRunName: string | null;
  methodName: string;
  conditionName: string;
  replicateNumber: number;
}

interface GetSimpleBenchlingNodeDataArgs {
  nodeType: string;
  methodName: string;
  procedureRunName: string | null;
  conditionName: string;
  replicateNumber: number;
}

function getSimpleBenchlingNodeData({
  nodeType,
  methodName,
  procedureRunName,
  conditionName,
  replicateNumber,
}: GetSimpleBenchlingNodeDataArgs): SimpleBenchlingNodeData {
  return {
    nodeHandleCustomizations: {
      nodeHeaderHandleRenderer: ({nodeId, handleType}) => (
        <InvisibleNodeHandle
          nodeId={nodeId}
          handleType={handleType}
          nodeHeaderHandleIdGenerator={(nodeId: string, handleType: HandleType) =>
            // All nodes have source handles, but only dest nodes have target handles.
            nodeType === NodeType.DESTINATION_PMEI || handleType === 'source'
              ? `${nodeId}-${handleType}`
              : null
          }
        />
      ),
    },
    hideSectionSummaries: true,
    nodeName: methodName,
    nodeSecondaryInfo: procedureRunName ?? '',
    sections: [
      {
        sectionName: `${conditionName} - ${replicateNumber}`,
        sectionRows: [],
      },
    ],
  };
}

export default function UnitOperationFlowchartNode({
  data,
  ...props
}: NodeProps<UnitOperationFlowchartNodeData>): JSX.Element {
  const {methodName, procedureRunName, conditionName, replicateNumber} = data;

  const simpleBenchlingNodeData = getSimpleBenchlingNodeData({
    nodeType: props.type,
    methodName,
    procedureRunName,
    conditionName,
    replicateNumber,
  });

  return (
    <div className={css(styles.node)}>
      <SimpleBenchlingNode {...props} data={simpleBenchlingNodeData} />
    </div>
  );
}

const styles = StyleSheet.create({
  node: {
    ':hover': {
      cursor: 'default',
    },
  },
});
